import React from 'react';
import { Link } from 'react-router';
import { CheckCircle2, CircleDashed } from 'lucide-react';
import {
  COMMAND_CENTER_RESUME_SETUP_LABEL,
  COMMAND_CENTER_SETUP_COMPLETE_COPY,
  COMMAND_CENTER_SETUP_INCOMPLETE_COPY,
  COMMAND_CENTER_SETUP_TITLE,
} from '../../commandCenterCopy';
import { onboardingStepDefinition } from '../../onboardingSteps';
import { lscPath } from '../../routing';
import type { LscLeagueSetupProgress } from '../../types';

export interface CommandCenterSetupProgressCardProps {
  leagueId: string;
  setupProgress: LscLeagueSetupProgress | null;
  setupComplete: boolean;
  loading?: boolean;
}

const CommandCenterSetupProgressCard: React.FC<CommandCenterSetupProgressCardProps> = ({
  leagueId,
  setupProgress,
  setupComplete,
  loading = false,
}) => {
  if (loading && !setupProgress) {
    return (
      <div
        className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-3 text-sm text-secondary-400"
        data-testid="command-center-setup-progress-loading"
      >
        Loading setup progress…
      </div>
    );
  }

  const resumeStepId = setupProgress?.resume_step_id;
  const recommendedStep = resumeStepId ? onboardingStepDefinition(resumeStepId) : null;
  const setupHref = lscPath(
    `/leagues/${leagueId}/setup${resumeStepId ? `?step=${resumeStepId}` : ''}`
  );

  return (
    <div
      className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-4 space-y-3"
      data-testid="command-center-setup-progress-card"
    >
      <div className="flex items-start gap-3">
        {setupComplete ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
        ) : (
          <CircleDashed className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
        )}
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-secondary-500">{COMMAND_CENTER_SETUP_TITLE}</p>
          <p className="text-sm font-medium text-secondary-100" data-testid="command-center-setup-status">
            {setupComplete ? 'Setup complete' : 'Setup incomplete'}
          </p>
          <p className="text-sm text-secondary-400">
            {setupComplete ? COMMAND_CENTER_SETUP_COMPLETE_COPY : COMMAND_CENTER_SETUP_INCOMPLETE_COPY}
          </p>
          {!setupComplete && recommendedStep ? (
            <p className="text-sm text-secondary-300" data-testid="command-center-setup-recommended-step">
              Recommended next step: <span className="text-white">{recommendedStep.label}</span>
            </p>
          ) : null}
        </div>
      </div>
      <Link
        to={setupHref}
        className="btn-secondary inline-flex text-sm"
        data-testid="command-center-setup-wizard-link"
      >
        {setupComplete ? 'Open setup wizard' : COMMAND_CENTER_RESUME_SETUP_LABEL}
      </Link>
    </div>
  );
};

export default CommandCenterSetupProgressCard;
